import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import useAlerts from "../useAlerts";
import useModals from "../useModals";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";
import ProductSizeService from "../../services/product/productSizeService";

import {
    loadingProductSizes,
    loadingProductSizesDropdown,
    toggleStateProductSize,
    addProductSize,
    updateProductSize,
    updateFormField,
    setForm,
    resetForm,
    setFormErrors,
    clearFormErrors,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
} from "../../store/slices/product/productSizeSlice";

import { IProductSizeFormValues } from "../../types/product/productSizeInterfaces";

export const useProductSizes = () => {
    const dispatch: AppDispatch = useDispatch();
    const { show: showAlert } = useAlerts();
    const { openModal } = useModals();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { handle } = useApiErrorHandler();

    // ===============================
    // GLOBAL STATE
    // ===============================
    const {
        productSizes,
        productSizesDropdown,
        form,
        isSubmit,
        formErrors,
        isCreateModalOpen,
        isEditModalOpen,
    } = useSelector((state: RootState) => state.productSizes);

    // =========================================================
    // GET ALL
    // =========================================================
    const fetchProductSizes = async () => {
        try {
            showLoadingIndicator();
            const response = await ProductSizeService.getAll();
            const { statusCode, data } = response;
            if (statusCode === 200) {
                dispatch(loadingProductSizes(data));
            }
        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    // =========================================================
    // DROPDOWN
    // =========================================================
    const fetchProductSizesDropdown = async () => {
        try {
            const response = await ProductSizeService.getDropdown();
            const { statusCode, data } = response;
            if (statusCode === 200) {
                dispatch(loadingProductSizesDropdown(data || []));
            }
        } catch (error) {
            handle(error);
        }
    };

    // =========================================================
    // DROPDOWN SEARCH
    // =========================================================
    const searchProductSizesDropdown = async (query: string) => {
        try {
            const response = await ProductSizeService.searchDropdown(query);
            const { statusCode, data } = response;
            if (statusCode === 200) {
                dispatch(loadingProductSizesDropdown(data || []));
            }
        } catch (error) {
            handle(error);
        }
    };


    // =========================================================
    // SEARCH
    // =========================================================
    const handlerSearchSizes = async (query: string) => {
        try {
            showLoadingIndicator();
            const response = await ProductSizeService.search(query);
            const { statusCode, data } = response;
            if (statusCode === 200) {
                dispatch(loadingProductSizes(data || []));
            }
        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    // =========================================================
    // SAVE / UPDATE
    // =========================================================
    const handlerAddProductSize = async (data: IProductSizeFormValues) => {
        try {
            if (data.id === 0) {
                // CREATE
                const { id, ...dto } = data;
                const response = await ProductSizeService.save(dto);

                const { statusCode, type, title, message, data: newSize } = response;

                if (statusCode === 200) {
                    dispatch(addProductSize(newSize));
                    dispatch(resetForm());
                    dispatch(closeCreateModal());
                    openModal(type, { title, message });
                }

            } else {
                // UPDATE
                const { id, ...dto } = data;
                const response = await ProductSizeService.update(id, dto);
                const { statusCode, type, title, message, data: updatedSize } = response;
                if (statusCode === 200) {
                    dispatch(updateProductSize(updatedSize));
                    dispatch(resetForm());
                    dispatch(closeEditModal());
                    openModal(type, { title, message });
                }
            }
        } catch (error: any) {
            handle(error);
        }
    };

    // =========================================================
    // LOAD SIZE BY ID AND OPEN EDIT MODAL
    // =========================================================
    const loadProductSizeForEdit = async (id: number) => {
        try {
            showLoadingIndicator();

            const response = await ProductSizeService.getById(id);
            const { statusCode, data } = response;

            if (statusCode === 200 && data) {
                dispatch(setForm(data));
                dispatch(clearFormErrors());
                dispatch(openEditModal());
            }
        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    // =========================================================
    // TOGGLE STATUS
    // =========================================================
    const toggleProductSizeStatus = async (id: number) => {
        try {
            showLoadingIndicator();
            const response = await ProductSizeService.toggleStatus(id);

            const { statusCode, type, title, message, data } = response;

            if (statusCode === 200) {
                dispatch(toggleStateProductSize(data));
                showAlert(type, title, message);
            }
        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    // =========================================================
    // OPEN EDIT MODAL (solo abrir)
    // =========================================================
    const openEditProductSize = () => {
        dispatch(openEditModal());
    };

    // =========================================================
    // CLOSE EDIT MODAL
    // =========================================================
    const closeEditProductSize = () => {
        dispatch(closeEditModal());
        dispatch(resetForm());
        dispatch(clearFormErrors());
    };

    // =========================================================
    // OPEN CREATE MODAL (clean form)
    // =========================================================
    const openCreateProductSize = () => {
        dispatch(resetForm());
        dispatch(clearFormErrors());
        dispatch(openCreateModal());
    };

    // =========================================================
    // CLOSE CREATE MODAL
    // =========================================================
    const closeCreateProductSize = () => {
        dispatch(closeCreateModal());
        dispatch(resetForm());
        dispatch(clearFormErrors());
    };

    // =========================================================
    // UPDATE FORM FIELD
    // =========================================================
    const updateField = (field: string, value: string) => {
        dispatch(updateFormField({ field, value }));
    };

    // =========================================================
    // FORM ERRORS HANDLERS
    // =========================================================
    const saveFormErrors = (errors: Record<string, string>) => {
        dispatch(setFormErrors(errors));
    };

    const clearErrors = () => {
        dispatch(clearFormErrors());
    };

    // =========================================================
    // RESET FORM FROM ANYWHERE
    // =========================================================
    const resetFormState = () => {
        dispatch(resetForm());
        dispatch(clearFormErrors());
    };


    return {
        // state
        productSizes,
        productSizesDropdown,
        form,
        isSubmit,
        formErrors,
        isCreateModalOpen,
        isEditModalOpen,

        // crud
        fetchProductSizes,
        fetchProductSizesDropdown,
        searchProductSizesDropdown,
        handlerSearchSizes,
        handlerAddProductSize,
        loadProductSizeForEdit,
        toggleProductSizeStatus,

        // ui
        openEditProductSize,
        closeEditProductSize,
        openCreateProductSize,
        closeCreateProductSize,

        // form
        updateField,
        saveFormErrors,
        clearErrors,
        resetFormState,
    };
};
